/* =====================================================================
   AstaLab 2.0 — ArtifactDemoCatalogRepository (round alpha.2, obiettivo 11-12)

   Implementazione locale/mock di CatalogRepository, usata SOLO
   nell'Artifact (stesso principio di ArtifactDemoBackendAdapter,
   round alpha.1) — la business logic (parsing, riconciliazione dello
   snapshot listone, copertura, dati mancanti, riconciliazione dei
   record esterni) è la STESSA di SupabaseCatalogRepository: entrambe
   chiamano gli stessi moduli puri in frontend/src/catalog/*Engine.js,
   mai una copia locale degli algoritmi (obiettivo 11).

   Nessuna finta persistenza cloud: tutto lo stato vive in localStorage,
   sotto una chiave dedicata, mai condivisa con lo store demo delle aste
   (ArtifactDemoBackendAdapter) — il catalogo è un dato GLOBALE AstaLab,
   mai duplicato dentro un'asta (vedi ARCHITECTURE_2.md, "Dati globali
   vs dati dell'asta").
   ===================================================================== */
import { CatalogRepository } from "./CatalogRepository.js";
import { reconcileCatalogSnapshot, listCurrentPlayers } from "./catalogSnapshotEngine.js";
import {
  applyStatisticsImport, computeMultiseasonCoverage, computeMissingDataRows,
  getPlayerSeasonStats, getStatisticsTargetSeasons, parseCSV, detectDelimiter, computeFileChecksum,
  validateListoneApplyContract, validateStatisticsApplyContract
} from "./statisticsEngine.js";
import {
  reconcileAllExternalRecords, setActiveExternalSource,
  resolveExternalRecordPlayer, EXTERNAL_SOURCE_WHITELIST, parseAndValidateExternalStatisticsCSV,
  assignReceiptSequences, externalIdEntries, setExternalIdEntry, removeExternalIdEntry,
  isVendorScopedExternalSource, sameProviderKey
} from "./externalRecordsEngine.js";
import { mapAndValidateProviderObservations, canonicalizeProviderKey, validateProviderKeyFormat } from "./providerEnrichmentEngine.js";
import { toStr, nowISO } from "./normalizeUtils.js";
import { ROLES } from "../engine/astalabEngine.js";

const STORAGE_KEY = "astalab_2_demo_catalog_v1";

function freshCatalogState(){
  return {
    players: [],
    statisticsArchives: {},
    statisticsConflicts: [],
    externalStatistics: [],
    unassociatedExternalStatistics: [],
    currentSeasonKey: null,
    statisticsTargetSeasonsOverride: null,
    ignoredMissingDataEntries: [],
    // Revisione r2 (blocker 3, parità con SupabaseCatalogRepository): identità file per l'import
    // esterno — un reimport dello stesso identico CSV è un no-op, mai una duplicazione.
    externalImportChecksums: [],
    // Revisione r9 (audit, blocker idempotenza listone): ricevute degli import listone riusciti,
    // chiave di idempotenza IDENTICA al cloud (kind + seasonKey + fileChecksum, mai un checksum
    // globale isolato — vedi applyListoneSnapshot()). Object.assign(freshCatalogState(), ...) in
    // loadCatalogState() garantisce che uno store r8 esistente (privo di questa chiave) la ottenga
    // comunque vuota al primo caricamento, senza invalidare nient'altro.
    catalogImportReceipts: [],
    // Revisione alpha.3-r3 (audit blocker A su r2): contatore persistito per receiptSeq — un ordine
    // di RICEZIONE reale e monotono per ogni record esterno nuovo (mai un id/UUID casuale), che deve
    // sopravvivere a un ricaricamento della pagina (altrimenti un batch importato dopo un reload
    // potrebbe ricevere receiptSeq PIÙ BASSI di quelli già persistiti, invertendo l'ordine reale).
    // Object.assign(freshCatalogState(), ...) in loadCatalogState() garantisce che uno store
    // pre-r3 (privo di questa chiave) parta comunque da 1, senza invalidare nient'altro.
    nextReceiptSeq: 1
  };
}
function loadCatalogState(){
  try{
    const raw = (typeof localStorage !== "undefined") ? localStorage.getItem(STORAGE_KEY) : null;
    if(!raw) return freshCatalogState();
    return Object.assign(freshCatalogState(), JSON.parse(raw));
  }catch(e){ return freshCatalogState(); }
}
function saveCatalogState(state){
  try{
    if(typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }catch(e){ /* storage pieno/non disponibile: la sessione demo resta solo in memoria per questo giro */ }
}

export class ArtifactDemoCatalogRepository extends CatalogRepository{
  constructor(){
    super();
    this._state = loadCatalogState();
  }
  get kind(){ return "artifact-demo"; }
  _persist(){ saveCatalogState(this._state); }

  async listCurrentPlayers(){
    return listCurrentPlayers(this._state);
  }
  async getPlayer(playerId){
    return this._state.players.find(p=>p.id===playerId) || null;
  }

  // Revisione r7 (audit, gap di parità repository Artifact/Supabase): PRIMA di questa correzione
  // questa frontiera si fidava ciecamente di QUALUNQUE records[]/seasonInfo ricevuto dal chiamante —
  // fantacalcioId duplicati, stagione assente/non canonica, ruolo/squadra/qtA/qtI/fvm mancanti erano
  // tutti accettati con ok:true (Id duplicati creavano persino due player con lo stesso fantacalcioId
  // e due UUID diversi). Il gap era chiuso SOLO se il chiamante passava dalla preview UI strict
  // (r5/r6) — un chiamante che costruisce records[] a mano otteneva un esito diverso da quello
  // dell'RPC Supabase per lo STESSO input. validateListoneApplyContract() applica ora ESATTAMENTE lo
  // stesso contratto qui, PRIMA di reconcileCatalogSnapshot()/reconcileAllExternalRecords()/_persist():
  // un input invalido non tocca MAI this._state.
  // Revisione r8 (audit, gap residuo di parità): applyListoneSnapshot() accettava fileChecksum
  // solo implicitamente (mai richiesto, mai passato al validator) — il contratto pubblico ora
  // richiede ESPLICITAMENTE un quarto argomento fileChecksum (stringa non vuota), esattamente come
  // SupabaseCatalogRepository/la RPC import_catalog_snapshot() già richiedono. Il repository non lo
  // genera MAI da solo: un checksum sintetico "inventato" qui nasconderebbe un chiamante che ha
  // dimenticato di calcolarlo, l'esatto comportamento che questa correzione elimina.
  // Revisione r9 (audit, blocker idempotenza listone): PRIMA di questa correzione fileChecksum era
  // obbligatorio (r8) ma poi completamente IGNORATO — un secondo import identico (stesso season +
  // stesso checksum) rieseguiva reconcileCatalogSnapshot()/reconcileAllExternalRecords()/_persist()
  // per davvero (player.updatedAt cambiava, "invariati" invece di "skipped:true"), mentre il cloud
  // (import_catalog_snapshot(), 0008) intercetta la STESSA combinazione kind='listone'+season_key+
  // file_checksum PRIMA di qualunque scrittura e restituisce skipped:true senza mutazioni. La chiave
  // di idempotenza qui è ESATTAMENTE la stessa del cloud — kind+seasonKey+fileChecksum, MAI un
  // checksum isolato: stesso checksum su una stagione diversa, o la stessa stagione con un checksum
  // diverso, restano sempre un import genuino, mai deduplicati fra loro.
  async applyListoneSnapshot(records, sourceFile, seasonInfo, fileChecksum){
    const contractError = validateListoneApplyContract(records, seasonInfo, fileChecksum);
    if(contractError) return { ok:false, summary:null, error:contractError };
    const receipts = this._state.catalogImportReceipts || [];
    const existingReceipt = receipts.find(r => r.kind==="listone" && r.seasonKey===seasonInfo.seasonKey && r.fileChecksum===fileChecksum);
    if(existingReceipt){
      // Zero mutazioni: nessuna chiamata a reconcileCatalogSnapshot()/reconcileAllExternalRecords()/
      // _persist() — currentSeasonKey, players, externalStatistics, unassociatedExternalStatistics,
      // catalogImportReceipts e il localStorage sottostante restano BYTE PER BYTE identici a prima.
      return { ok:true, summary: Object.assign({}, existingReceipt.summary, { skipped:true, reason:"duplicate_file" }), error:null };
    }
    const summary = reconcileCatalogSnapshot(this._state, records, sourceFile, seasonInfo);
    const externalReconciliation = reconcileAllExternalRecords(this._state);
    const fullSummary = Object.assign({}, summary, {externalReconciliation});
    // La receipt viene registrata SOLO qui, dopo che reconcileCatalogSnapshot()/
    // reconcileAllExternalRecords() sono già andati a buon fine — mai per un import fallito
    // (contractError sopra ritorna prima di arrivare qui) e mai prima che il summary sia stato
    // costruito con successo.
    this._state.catalogImportReceipts = receipts.concat([{
      kind: "listone", seasonKey: seasonInfo.seasonKey, fileChecksum, sourceFile: sourceFile || "",
      summary: fullSummary, importedAt: nowISO()
    }]);
    this._persist();
    return { ok:true, summary: fullSummary, error:null };
  }

  // Revisione r8 (audit, blocker 1): guardia difensiva esplicita PRIMA del validator condiviso —
  // preview.ok!==true (false/0/null/undefined/qualunque valore non letteralmente true) deve fallire
  // qui, con lo stesso identico esito di SupabaseCatalogRepository.applyStatisticsImport() (che aveva
  // già questo controllo). validateStatisticsApplyContract() la verifica anch'essa (backstop
  // condiviso, mai rimosso), ma questa riga rende esplicito, leggibile qui, l'invariante "una
  // preview.ok!==true non deve MAI raggiungere il motore" — un fallimento del validator condiviso non
  // baserebbe MAI da solo su questa unica riga.
  async applyStatisticsImport(preview){
    if(!preview || preview.ok!==true) return { ok:false, summary:null, error:"Preview non valida." };
    const contractError = validateStatisticsApplyContract(preview);
    if(contractError) return { ok:false, summary:null, error:contractError };
    const summary = applyStatisticsImport(this._state, preview);
    if(!summary.skipCommit) this._persist();
    return { ok:true, summary, error:null };
  }

  async getCoverage(seasonKeys){
    return computeMultiseasonCoverage(this._state, seasonKeys);
  }
  async getMissingDataRows(seasonKeys, options){
    return computeMissingDataRows(this._state, seasonKeys, options);
  }
  async getPlayerSeasonStats(playerId, seasonKey){
    const player = this._state.players.find(p=>p.id===playerId);
    if(!player) return null;
    return getPlayerSeasonStats(player, seasonKey, this._state);
  }
  async getStatisticsTargetSeasons(){
    return getStatisticsTargetSeasons(this._state);
  }

  // Round 2.2.0: nessun controllo admin interno esiste in ArtifactDemoCatalogRepository per
  // NESSUNA operazione (identico a applyListoneSnapshot/applyStatisticsImport) — sempre true, la UI
  // di gestione demo è quindi sempre visibile in modalità Artifact, per costruzione.
  async isCurrentUserAdmin(){ return true; }

  // Round 2.2.0 (risoluzione dei conflitti statistici UFFICIALI). this._state.statisticsConflicts è
  // il registro REALE già scritto da applyStatisticsImport()/mergeStatisticsRecord() sopra
  // (statisticsEngine.js) — per il backend demo, a differenza di Supabase, questo È il registro
  // persistito equivalente a public.statistics_conflicts (nessuna tabella separata nel modello
  // demo/localStorage): mai un duplicato o una simulazione a parte.
  async listOfficialStatisticsConflicts(){
    return (this._state.statisticsConflicts || []).filter(c => c.status === "open");
  }

  // Stessa allowlist chiusa e stessi range di validazione ESATTI di import_statistics_archive()
  // (0008)/normalizeStatisticsRecord() sopra — mai un clamp: toIntegerOrNull/toNumOrNull non sono
  // riusabili qui perché arrotondano/troncano silenziosamente un valore fuori range, esattamente
  // ciò che questa risoluzione non deve mai fare (un valore fuori range viene RIFIUTATO, mai
  // corretto alla cieca). Nota sui nomi di campo: il modello demo/JS usa nomi diversi dalle colonne
  // Postgres per gli stessi concetti (squadra/ruolo/rigoriSegnati/rigoriSbagliati/assist/
  // ammonizioni/espulsioni/autogol invece di team/role/penalties_scored/penalties_missed/assists/
  // yellow_cards/red_cards/own_goals) — stesso identico significato/range, mai gli stessi letterali
  // della RPC Supabase (0021), che opera sui nomi di colonna reali.
  _validateResolvedFieldValue(field, rawValue){
    if(field === "squadra"){
      const v = (rawValue == null) ? "" : String(rawValue).trim();
      if(v === "") return { ok:false, error:"Valore squadra non valido (vuoto)." };
      return { ok:true, value:v };
    }
    if(field === "ruolo"){
      if(rawValue == null || !ROLES.includes(rawValue)) return { ok:false, error:`Valore ruolo non valido "${rawValue}".` };
      return { ok:true, value:rawValue };
    }
    const RANGES = {
      pv: [0, 60, true], mv: [0, 10, false], fm: [-50, 50, false],
      gf: [0, 200, true], gs: [0, 300, true], rp: [0, 100, true], rc: [0, 100, true],
      rigoriSegnati: [0, 100, true], rigoriSbagliati: [0, 100, true],
      assist: [0, 200, true], ammonizioni: [0, 200, true], espulsioni: [0, 50, true], autogol: [0, 50, true]
    };
    const range = RANGES[field];
    if(!range) return { ok:false, error:`Campo "${field}" non supportato per la risoluzione.` };
    const n = (rawValue == null || rawValue === "") ? NaN : Number(rawValue);
    const [min, max, mustBeInteger] = range;
    if(!Number.isFinite(n) || n < min || n > max || (mustBeInteger && n !== Math.floor(n))){
      return { ok:false, error:`Valore "${field}" fuori range/non valido (${min}..${max}${mustBeInteger?", intero":""}).` };
    }
    return { ok:true, value:n };
  }

  // Stessa disciplina "stale-safe" (confronto tipizzato + mai una sovrascrittura silenziosa di un
  // dato più recente) della RPC Supabase resolve_statistics_conflict() (0021) — qui il confronto è
  // diretto sui valori JS già tipizzati (mai testo, a differenza di Postgres dove existing_value/
  // incoming_value sono colonne TEXT), quindi non serve il cast numerico esplicito richiesto lato
  // SQL. Nessun controllo admin qui: il modello demo/ArtifactDemoCatalogRepository non ha mai
  // implementato un controllo di autorizzazione interno per NESSUna operazione admin-only (identico
  // a applyListoneSnapshot/applyStatisticsImport sopra) — l'admin-only del round 2.2.0 è applicato
  // dalla UI (CatalogPage.js) qui, e dalla RPC reale (private.is_admin()) su Supabase.
  async resolveStatisticsConflict(conflictId, action, customValue){
    if(!["keep_current", "accept_incoming", "custom_value", "ignore"].includes(action)){
      return { ok:false, status:null, resolvedValue:null, error:`Azione non valida "${action}": ammesse solo keep_current, accept_incoming, custom_value, ignore.` };
    }
    if(action === "custom_value"){
      if(customValue == null) return { ok:false, status:null, resolvedValue:null, error:"Il valore personalizzato è obbligatorio per l'azione custom_value." };
    } else if(customValue != null){
      return { ok:false, status:null, resolvedValue:null, error:`Il valore personalizzato deve essere assente per l'azione "${action}".` };
    }

    const conflicts = (this._state.statisticsConflicts || []).slice();
    const idx = conflicts.findIndex(c => c.id === conflictId);
    if(idx === -1) return { ok:false, status:null, resolvedValue:null, error:`Conflitto ${conflictId} non trovato.` };
    const conflict = conflicts[idx];
    if(conflict.status !== "open"){
      return { ok:false, status:null, resolvedValue:null, error:`Il conflitto ${conflictId} non è aperto (stato attuale: ${conflict.status}).` };
    }

    if(action === "ignore"){
      conflicts[idx] = Object.assign({}, conflict, { status:"ignored", resolution:null, resolvedValue:null, resolvedAt: nowISO() });
      this._state.statisticsConflicts = conflicts;
      this._persist();
      return { ok:true, status:"ignored", resolvedValue:null, error:null };
    }

    const archive = this._state.statisticsArchives[conflict.seasonKey];
    const target = archive && archive.players ? archive.players[conflict.fantacalcioId] : null;
    if(!target){
      return { ok:false, status:null, resolvedValue:null, error:`Riga statistiche ufficiali non trovata per fantacalcioId ${conflict.fantacalcioId} stagione ${conflict.seasonKey}.` };
    }

    const currentValue = target[conflict.field] == null ? null : target[conflict.field];
    const existingSnapshot = conflict.existingValue == null ? null : conflict.existingValue;
    if(currentValue !== existingSnapshot){
      return { ok:false, status:null, resolvedValue:null, error:`Conflitto ${conflictId} obsoleto: il valore servito per il campo "${conflict.field}" è già cambiato dal rilevamento.` };
    }

    let appliedValue;
    if(action === "keep_current"){
      appliedValue = currentValue;
    } else {
      const source = action === "accept_incoming" ? conflict.incomingValue : customValue;
      const validation = this._validateResolvedFieldValue(conflict.field, source);
      if(!validation.ok) return { ok:false, status:null, resolvedValue:null, error: validation.error };
      appliedValue = validation.value;
      const updatedArchive = Object.assign({}, archive, {
        players: Object.assign({}, archive.players, {
          [conflict.fantacalcioId]: Object.assign({}, target, { [conflict.field]: appliedValue })
        })
      });
      this._state.statisticsArchives = Object.assign({}, this._state.statisticsArchives, { [conflict.seasonKey]: updatedArchive });
    }

    const resolvedValueText = appliedValue == null ? null : String(appliedValue);
    conflicts[idx] = Object.assign({}, conflict, { status:"resolved", resolution:action, resolvedValue:resolvedValueText, resolvedAt: nowISO() });
    this._state.statisticsConflicts = conflicts;
    this._persist();
    return { ok:true, status:"resolved", resolvedValue:resolvedValueText, error:null };
  }

  async listExternalRecords(){
    return this._state.externalStatistics.slice();
  }
  async listUnassociatedExternalRecords(){
    // Arricchisce ogni record in coda con lo status/reason di risoluzione (matched-mai-attivo
    // escluso per definizione qui: unmatched/ambiguous/identity-conflict/invalid), ricalcolato al
    // volo per sola lettura via resolveExternalRecordPlayer() — la STESSA funzione pura che
    // reconcileAllExternalRecords() usa per decidere dove va ogni record, mai una spiegazione
    // duplicata o disallineata. Stessa forma (status/reason) dello schema cloud
    // (unassociated_external_statistics.status/reason, 0005), cosi' la UI può mostrare "perché"
    // un record non è (più) attivo indipendentemente dal backend in uso.
    return this._state.unassociatedExternalStatistics.map(r => {
      const resolution = resolveExternalRecordPlayer(this._state, r);
      return Object.assign({}, r, { status: resolution.status, reason: resolution.reason || "" });
    });
  }

  async importExternalStatisticsCSV(text, filename){
    // Revisione r2 (blocker 3): idempotenza per checksum, stesso principio delle altre RPC di
    // import — un reimport dello stesso identico CSV è un no-op, mai una duplicazione né una
    // perturbazione della selezione attiva già scelta dall'utente.
    const checksum = computeFileChecksum(new TextEncoder().encode(text));
    if(this._state.externalImportChecksums.includes(checksum)){
      return { ok:true, warnings:[], error:null, skipped:true };
    }
    // Revisione r4 (audit, blocker 2): parsing+validazione STRICT condivisa con
    // SupabaseCatalogRepository — un valore fuori range/non intero/non numerico fa fallire l'INTERO
    // file (mai un clamp silenzioso, mai un import parziale, mai un checksum segnato come importato
    // per un file rifiutato).
    const parsed = parseAndValidateExternalStatisticsCSV(text, parseCSV, detectDelimiter);
    if(!parsed.ok) return { ok:false, warnings:[], error:parsed.error };
    // Upsert per chiave (fantacalcioId+season+source+sourcePlayerId), poi riconciliazione completa
    // — stesso principio di importExternalStatisticsToCandidate() 1.3 (mai duplicati su reimport).
    // Revisione alpha.3-r3 (audit blocker A su r2): ogni record NUOVO riceve un receiptSeq reale e
    // monotono (ordine di ricezione nel file, mai un id/UUID casuale) — il contatore persistito
    // avanza SOLO qui, non nel motore puro (che resta senza stato nascosto).
    const { records: seqRecords, nextSeq } = assignReceiptSequences(parsed.records, this._state.nextReceiptSeq);
    seqRecords.forEach(rec=>{
      this._state.unassociatedExternalStatistics.push(rec);
    });
    this._state.nextReceiptSeq = nextSeq;
    const { warnings } = reconcileAllExternalRecords(this._state);
    this._state.externalImportChecksums = this._state.externalImportChecksums.concat([checksum]);
    this._persist();
    return { ok:true, warnings, error:null, skipped:false };
  }

  // Round alpha.3-r1 (fondamenta enrichment provider-neutral): STESSA coda/riconciliazione di
  // importExternalStatisticsCSV() sopra — mai una seconda pipeline. Riceve osservazioni RAW già
  // fetchate da un ProviderAdapter (mai un'istanza dell'adapter: questo repository non deve mai
  // conoscere trasporto/autenticazione di un provider), le mappa/valida STRICT
  // (providerEnrichmentEngine.js — un solo campo fuori contratto rifiuta l'INTERO batch, mai un
  // import parziale, stesso principio di parseAndValidateExternalStatisticsCSV) e SOLO se valide le
  // accoda per la riconciliazione. Idempotenza per checksum del batch, stessa lista
  // externalImportChecksums già usata per l'import CSV — concettualmente lo stesso "ho già
  // processato esattamente questo import", non un secondo meccanismo.
  // Revisione alpha.3-r4 (blocker 5, terzo audit): il checksum di idempotenza deve essere
  // PROVIDER-SCOPED — prima di questa revisione veniva calcolato SOLO sul payload raw, quindi due
  // vendor diversi che inviassero (per coincidenza o test) lo stesso identico JSON avrebbero
  // condiviso la stessa identità di import, e il secondo sarebbe stato erroneamente marcato
  // "skipped" a causa del primo. Canonicalizzato PRIMA di entrare nel checksum (whitespace/
  // maiuscole del providerKey non devono mai creare due namespace per lo stesso vendor).
  async importProviderObservations(rawObservations, providerKey){
    const canonicalProviderKey = canonicalizeProviderKey(providerKey);
    const checksum = computeFileChecksum(new TextEncoder().encode(JSON.stringify({ providerKey: canonicalProviderKey, observations: rawObservations || [] })));
    if(this._state.externalImportChecksums.includes(checksum)){
      return { ok:true, warnings:[], error:null, skipped:true };
    }
    const mapped = mapAndValidateProviderObservations(rawObservations, providerKey);
    if(!mapped.ok) return { ok:false, warnings:[], error:mapped.error };
    const { records: seqRecords, nextSeq } = assignReceiptSequences(mapped.records, this._state.nextReceiptSeq);
    seqRecords.forEach(rec=>{
      this._state.unassociatedExternalStatistics.push(rec);
    });
    this._state.nextReceiptSeq = nextSeq;
    const { warnings } = reconcileAllExternalRecords(this._state);
    this._state.externalImportChecksums = this._state.externalImportChecksums.concat([checksum]);
    this._persist();
    return { ok:true, warnings, error:null, skipped:false };
  }

  // Revisione alpha.3-r3 (audit blocker B su r2): `providerKey` (opzionale, quinto parametro — mai
  // rompe una chiamata esistente a 4 argomenti) seleziona QUALE vendor per una fonte "vendor-scoped"
  // (oggi solo "provider-authorized" — vedi isVendorScopedExternalSource()): un giocatore può avere
  // un'associazione manuale PER vendor, tutte coesistenti (mai una sostituisce l'altra, a differenza
  // di prima di questa revisione, dove player.externalIds["provider-authorized"] era uno scalare
  // singolo — vendor B sovrascriveva sempre vendor A). Per csv-manual/transfermarkt-manual il
  // comportamento resta ESATTAMENTE quello di sempre (uno scalare, providerKey ignorato/vietato).
  async associateExternalId(playerId, source, sourcePlayerId, sourceUrl, providerKey){
    if(!EXTERNAL_SOURCE_WHITELIST.includes(source)) return { ok:false, error:"Fonte non consentita." };
    const trimmedId = toStr(sourcePlayerId,"").trim();
    if(!trimmedId) return { ok:false, error:"Identificativo obbligatorio." };
    const vendorScoped = isVendorScopedExternalSource(source);
    const canonicalProviderKey = providerKey==null ? null : canonicalizeProviderKey(providerKey);
    if(!vendorScoped && canonicalProviderKey){
      return { ok:false, error:"providerKey non è consentito per la fonte \""+source+"\" (solo per fonti vendor-scoped)." };
    }
    if(vendorScoped){
      if(!canonicalProviderKey) return { ok:false, error:"providerKey obbligatorio per un'associazione \""+source+"\" (identità vendor-scoped)." };
      const providerKeyErr = validateProviderKeyFormat(canonicalProviderKey);
      if(providerKeyErr) return { ok:false, error:providerKeyErr };
    }
    const player = this._state.players.find(p=>p.id===playerId);
    if(!player) return { ok:false, error:"Giocatore non trovato." };
    // Revisione alpha.3-r4 (obiettivo architetturale + multi-vendor identity): il conflitto è ora
    // scoping per VENDOR (source + providerKey), mai per il solo valore grezzo — due vendor diversi
    // possono legittimamente condividere lo stesso id grezzo su due giocatori diversi (mai più
    // impedito dalla namespacing della stringa, rimossa in questo round).
    const conflicting = this._state.players.find(p=>p.id!==playerId && externalIdEntries(p).some(e=>e.source===source && e.value===trimmedId && sameProviderKey(e.providerKey, canonicalProviderKey)));
    if(conflicting) return { ok:false, error:"L'identificativo "+trimmedId+" su "+source+" è già associato a "+conflicting.nome+"." };
    setExternalIdEntry(player, source, canonicalProviderKey, trimmedId);
    // Revisione alpha.3-r5 (quarto audit indipendente, BLOCKER C): la propagazione del sourceUrl
    // controllava SOLO (source, sourcePlayerId) — con il modello separato r4 due vendor diversi
    // possono legittimamente condividere lo stesso sourcePlayerId grezzo (es. vendor-a/123 e
    // vendor-b/123), quindi associare A con un sourceUrl faceva finire quell'URL anche sulla riga
    // in coda del vendor B (stesso source, stesso sourcePlayerId grezzo, providerKey ignorato).
    // Corretto aggiungendo sameProviderKey(r.providerKey, canonicalProviderKey) al filtro — per le
    // fonti legacy (canonicalProviderKey sempre null, r.providerKey sempre null) equivale ESATTAMENTE
    // al comportamento di prima (nessuna regressione).
    if(sourceUrl){
      this._state.unassociatedExternalStatistics.forEach(r=>{ if(r.source===source && r.sourcePlayerId===trimmedId && sameProviderKey(r.providerKey, canonicalProviderKey) && !r.sourceUrl) r.sourceUrl = sourceUrl; });
    }
    reconcileAllExternalRecords(this._state);
    this._persist();
    return { ok:true, error:null };
  }

  async removeExternalId(playerId, source, providerKey){
    if(!EXTERNAL_SOURCE_WHITELIST.includes(source)) return { ok:false, error:"Fonte non consentita." };
    const player = this._state.players.find(p=>p.id===playerId);
    if(!player) return { ok:false, error:"Giocatore non trovato." };
    const canonicalProviderKey = providerKey==null ? null : canonicalizeProviderKey(providerKey);
    const removed = removeExternalIdEntry(player, source, canonicalProviderKey || null);
    if(!removed) return { ok:true, skipCommit:true, error:null };
    reconcileAllExternalRecords(this._state);
    this._persist();
    return { ok:true, error:null };
  }

  async setActiveExternalSource(recordId){
    const r = setActiveExternalSource(this._state, recordId);
    if(r.ok) this._persist();
    return r;
  }
}
